import axios from 'axios';

/**
 * companyService (ui-design copy)
 * ------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 *
 * The `bay-email-editor` submodule (src/bay-email-editor) is checked out
 * into BOTH the `client` app and this `ui-design` app. Inside the submodule,
 * PreviewModal imports `companyService` from the HOST app via the `~` alias:
 *
 *     import companyService from '~/services/companyService';   // ~  ->  <host>/src
 *
 * (Same host-provides-the-service pattern the submodule uses for `kvstore`.)
 *
 * `client` ships its own src/services/companyService.js, so the import resolves
 * there. `ui-design` did NOT — so the import dangled and broke the build here.
 * This file is that missing host-side implementation for ui-design.
 *
 * The only method the email editor actually calls is `getCampaignDisclaimer`
 * (PreviewModal -> fetchCampaignDisclaimerOnce), so that's all we implement
 * here — YAGNI on the rest of client's companyService (logo upload, color
 * lock, etc.). Add more methods only if the submodule starts needing them.
 *
 * NOTE ON RUNTIME BEHAVIOR IN UI-DESIGN:
 * ui-design previews run as design-admin (isGlobalRole), and PreviewModal
 * skips the disclaimer fetch entirely for admins — it keeps the raw
 * {{campaignDisclaimer}} tag. So in practice this endpoint is rarely (if ever)
 * hit from ui-design; the import just has to resolve. If it IS called and the
 * backend isn't reachable, PreviewModal's .catch swallows it and falls back to
 * a null disclaimer, so nothing breaks.
 *
 * Long-term fix (not done here — would touch both hosts): stop the shared
 * editor reaching into host services and inject the resolved disclaimer through
 * the existing previewInjectData / mergeTags contract instead.
 * ------------------------------------------------------------------
 */

/**
 * @param {{ url: string, method: string, data?: object, params?: object }} args
 * @returns {Promise<{ data: any } | { error: { status: any, data: any } }>}
 * @private
 */
const axiosBaseQuery = async ({ url, method, data, params }) => {
	try {
		const apiResponse = await axios({ url, method, data, params });
		return { data: apiResponse.data };
	} catch (axiosError) {
		const err = axiosError;
		return {
			error: {
				status: err.response?.status,
				data: err.response?.data || err.message,
			},
		};
	}
};

const companyService = () => {
	return {
		// Mirrors client/ui/src/services/companyService.js — keep the endpoint and
		// response shape ({ STATUS, campaignDisclaimer }) in sync with it.
		getCampaignDisclaimer: async () => {
			const response = await axiosBaseQuery({
				url: '/global/components/companies.cfc',
				method: 'GET',
				params: {
					method: 'getCampaignDisclaimer',
				},
			});
			if (response?.data?.STATUS !== 0) {
				throw new Error(
					response?.data?.MESSAGE || 'Failed to get campaign disclaimer',
				);
			}
			return response?.data;
		},
	};
};

const singleton = companyService();
Object.freeze(singleton);

export default singleton;
